import {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useContext,
    useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { CashierClosedBuyError } from '../services/buy'
import { createNewLoadStock } from '../services/product'
import { useAccount } from './account_context'
import { BranchInfo } from './branch_context'
import { useCashier } from './cashier_context'
import { useCoin } from './coin_context'
import { SystemBranchEnum, useConfigs } from './configs_context'
import { useLoading } from './loading_context'
import {
    LoadStockInfo,
    LoadStockStatus,
    ProductInfo,
    ProductInLoadStock
} from './product_context'
import { useSnack } from './snack_context'
const currency = require('currency.js')

const loadStockInfoDefault: LoadStockInfo = {
    products: [],
    branch: null,
    status: LoadStockStatus.Created,
}

export interface LoadStockContextInterface {
    loadStock: LoadStockInfo
    setLoadStock: Dispatch<SetStateAction<LoadStockInfo>>
    handleAddNewProducts(products: ProductInfo[]): void
    handleRemoveProduct(product: ProductInLoadStock): void
    handleChangeProductQnt(product: ProductInLoadStock, qnt: number): void
    handleChangeProductNegativeQnt(
        product: ProductInLoadStock,
        qnt: number
    ): void
    handleChangeBranch(branch: BranchInfo): void
    validate(): boolean
    handleCreateNewLoadStock(): Promise<void>
    resetContext(): void
}

const loadStockContext = createContext<LoadStockContextInterface>({
    loadStock: loadStockInfoDefault,
    setLoadStock: () => {},
    handleAddNewProducts: () => {},
    handleRemoveProduct: () => {},
    handleChangeProductQnt: () => {},
    handleChangeProductNegativeQnt: () => {},
    handleChangeBranch: () => {},
    validate: () => false,
    handleCreateNewLoadStock: async () => {},
    resetContext: () => {},
})

export function useLoadStock() {
    return useContext(loadStockContext)
}

interface Props {
    children: ReactNode
}

export const LoadStockContextProvider = ({ children }: Props) => {
    const loading = useLoading()
    const snack = useSnack()
    const navigate = useNavigate()
    const { coins } = useCoin()
    const [loadStock, setLoadStock] =
        useState<LoadStockInfo>(loadStockInfoDefault)
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()
    const {handleChangeCashierFromList} = useCashier()

    const handleAddNewProducts = (products: ProductInfo[]) => {
        const isMasterBranch =
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch
        if (isMasterBranch && !loadStock.branch) {
            snack.error('Seleccione una sucursal!')
            return
        }

        const newProducts = [...loadStock.products]
        let count = loadStock.productCount ?? 0

        products.forEach((product) => {
            const foundProduct = loadStock.products.find(
                (p) => p.product.id === product.id
            )
            if (foundProduct && foundProduct.product.autoSum) {
                handleChangeProductQnt(
                    foundProduct,
                    currency(foundProduct.qnt).add(1).value
                )
                return
            }
            if (!product.isAtive) {
                snack.error('O produto está desativado!')
                return
            }

            count += 1
            newProducts.push({
                product: product,
                qnt: 1,
                id: count,
                negativeQnt: 0,
            })
        })

        setLoadStock((prev) => ({
            ...prev,
            products: newProducts,
            productCount: count,
        }))
    }

    const handleRemoveProduct = (product: ProductInLoadStock) => {
        const newProducts = [...loadStock.products]
        const index = newProducts.findIndex((p) => p.id === product.id)
        if (index === -1) return

        newProducts.splice(index, 1)

        setLoadStock((prev) => ({
            ...prev,
            products: newProducts,
        }))
    }

    const handleChangeProductQnt = (
        product: ProductInLoadStock,
        qnt: number
    ) => {
        const newProducts = [...loadStock.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.qnt = qnt

        setLoadStock((prev) => ({
            ...prev,
            products: newProducts,
        }))
    }

    const handleChangeProductNegativeQnt = (
        product: ProductInLoadStock,
        qnt: number
    ) => {
        const newProducts = [...loadStock.products]
        const newProduct = newProducts.find((p) => p.id === product.id)
        if (!newProduct) return

        newProduct.negativeQnt = qnt

        setLoadStock((prev) => ({
            ...prev,
            products: newProducts,
        }))
    }

    const handleChangeBranch = (branch: BranchInfo) => {
        setLoadStock((prev) => ({
            ...prev,
            branch: branch,
            products: [],
        }))
        if (configs.chooseCashier) {
            handleChangeCashierFromList(branch.id)
        }
    }

    const validate = (): boolean => {
        if (!loadStock.products.length) {
            snack.error('Selecione um produto!')
            return false
        }
        if (loadStock.products.filter((p) => !p.qnt && !p.negativeQnt).length) {
            snack.error('O estoque não pode ser 0!')
            return false
        }
        if (
            !accountInfo?.branch &&
            configs.systemBranch === SystemBranchEnum.Branch &&
            !loadStock.branch
        ) {
            snack.error('Selecione uma filial!')
            return false
        }
        return true
    }

    const handleCreateNewLoadStock = async () => {
        loading.show()
        try {
            await createNewLoadStock({
                products: loadStock.products.map((p) => ({
                    id_produto: p.product?.id ?? 0,
                    quantidade_produto: p.qnt,
                    quantidade_produto_negativo: p.negativeQnt ?? 0,
                })),
                id_filial_master: loadStock.branch?.id,
                anotacao: loadStock.annotation || null
            })
            snack.success('Produtos carregados com sucesso!')
            navigate('/system/product/load-stock')
            setLoadStock({ ...loadStockInfoDefault })
            localStorage.removeItem('loadStockInfo')
        } catch (error) {
            if (error instanceof CashierClosedBuyError)
                snack.error('O caixa está fechado!')
            else snack.connectionFail(error)
        }
        loading.hide()
    }

    const resetContext = () => {
        setLoadStock({
            ...loadStockInfoDefault,
        })
    }

    const handleChangeSelectedProducts = (products: ProductInLoadStock[]) => {
        setLoadStock((prev) => ({
            ...prev,
            selectedProducts: products,
        }))
    }

    return (
        <loadStockContext.Provider
            value={{
                loadStock,
                setLoadStock,
                handleAddNewProducts,
                handleRemoveProduct,
                handleChangeProductQnt,
                handleChangeProductNegativeQnt,
                handleChangeBranch,
                validate,
                handleCreateNewLoadStock,
                resetContext,
            }}
        >
            {children}
        </loadStockContext.Provider>
    )
}
